import {
  FileUploadButton,
  Stack,
  Text,
  Box,
  IconButton,
} from "@origin-digital/ods-core";
import { IconError, IconClose } from "@origin-digital/ods-icons";
import React, { ChangeEvent, useContext } from "react";
import type { CloseTransferFile, FormValues, UploadedFile } from "../types";
import FormContext from "src/context/FormContext";
import { FieldProps, useField, useFormikContext } from "formik";
import { getNestedValue, normalizeFileName } from "../helper";
import { MessageAttachment } from "../Messages/types";
import { IconButtonColor } from "@origin-digital/ods-types/.";

interface FileUploadProps {
  heading?: string;
  field: FieldProps["field"];
}

const ALLOWED_FILE_TYPES = [
  "image/jpeg",
  "image/jpg",
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel", //XLS
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", //XLSX
  "application/vnd.ms-excel.sheet.macroenabled.12", //XLSM
  "image/png",
  "image/webp",
  "image/heic",
  "image/heif",
];

const FileUpload: React.FC<FileUploadProps> = ({ heading, field }) => {
  const { setFieldValue, values } = useFormikContext<FormValues>();
  const [, { error: validationError }] = useField(field.name);
  const { setDisplayingSnackbar } = useContext(FormContext);

  const files: MessageAttachment[] =
    getNestedValue(values, field.name as keyof FormValues) || [];

  const disabledUploadButton = files.length > 0;

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || files.length >= 2) return;

    // file type check
    if (!ALLOWED_FILE_TYPES.includes(file.type)) {
      setDisplayingSnackbar({
        message:
          "Accepted types: pdf, doc/docx, jpg/jpeg, png, heif, webp, xls/xlsm/xlsx.",
        tone: "critical",
      });
      event.target.value = "";
      return;
    }

    const attachedFile: CloseTransferFile = {
      fileName: normalizeFileName(file!.name),
      file,
    };

    setFieldValue(field.name, [attachedFile]);
  };

  const handleRemoveFile = () => {
    setFieldValue(field.name, []);
  };

  return (
    <Stack>
      {heading && <Text>{heading}</Text>}
      {files.length > 0 && (
        <Stack space="medium">
          {files.map((file, index) => {
            return (
              <UploadedFile
                key={index}
                file={file}
                handleRemoveFile={handleRemoveFile}
              />
            );
          })}
        </Stack>
      )}
      {!disabledUploadButton && (
        <Stack space="small">
          <FileUploadButton
            variant="text"
            size="small"
            onFileUpload={(e) => {
              handleFileChange(e);
            }}
          >
            {files.length === 0 ? "Upload Document" : "Upload Another Document"}
          </FileUploadButton>
          {/* Error helperText */}
          {validationError && (
            <Box display="flex" marginLeft="xsmall">
              <IconError color="critical" size="xsmall" />
              <Box marginLeft="xxsmall">
                <Text variant="body-small" tone="critical">
                  {validationError}
                </Text>
              </Box>
            </Box>
          )}
          {/* End Error helperTex */}
        </Stack>
      )}
    </Stack>
  );
};

interface UploadedFileProps {
  file: MessageAttachment;
  handleRemoveFile: () => void;
}
const UploadedFile: React.FC<UploadedFileProps> = ({
  file,
  handleRemoveFile,
}) => {
  return (
    <Box
      padding="medium"
      border="1px solid #F1F1F1"
      borderRadius="small"
      backgroundColor="grey50"
    >
      <Box display="flex" alignItems="flex-start">
        <Stack space="xxsmall">
          <Box display="flex" alignItems="center">
            {/* <Text weight="medium">{file.fileType}</Text> */}
            <Text wordBreak data-role="value">
              {file.fileName}
            </Text>
          </Box>
        </Stack>
        <Box display="inline-flex" marginLeft="xsmall">
          <IconButton
            noIconPadding
            aria-label="Remove file"
            color={"grey300" as IconButtonColor}
            onClick={() => handleRemoveFile()}
          >
            <IconClose size="xsmall" />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
};

export default FileUpload;
